import React from 'react';

import styled from '@emotion/styled';

type Variant = 'solid' | 'outline' | 'ghost' | 'tonal';
type Size = 'sm' | 'md' | 'lg';
type ColorScheme = 'primary' | 'secondary' | 'neutral' | 'danger' | 'success';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 시각적 변형: solid(채움), outline(테두리), ghost(투명), tonal(톤다운) */
  variant?: Variant;
  /** 색상군: primary(청록), secondary(보라), neutral(회색), danger(빨강), success(초록) */
  colorScheme?: ColorScheme;
  /** 크기: sm(36px), md(40px), lg(48px) */
  size?: Size;
  /** 모양: rounded(8px), pill(원형) */
  shape?: 'rounded' | 'pill';
  /** 로딩상태 */
  loading?: boolean;
  /** 왼쪽 아이콘 */
  iconLeft?: React.ReactNode;
  /** 오른쪽 아이콘 */
  iconRight?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'solid',
      colorScheme = 'primary',
      size = 'md',
      shape = 'rounded',
      loading = false,
      iconLeft,
      iconRight,
      disabled,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <SButton
        ref={ref}
        $variant={variant}
        $color={colorScheme}
        $size={size}
        $shape={shape}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        {...rest}
      >
        {iconLeft && <span className="btn-icon left">{iconLeft}</span>}
        <span className="btn-label">{children}</span>
        {iconRight && <span className="btn-icon right">{iconRight}</span>}
      </SButton>
    );
  }
);

Button.displayName = 'Button';

/* ---------- styles ---------- */

const getColorByScheme = (theme: any, colorScheme: ColorScheme, type: 'background' | 'text'): string => {
  if (type === 'background') {
    switch (colorScheme) {
      case 'primary':
        return theme.color.static.primary;
      case 'secondary':
        return theme.color.static.secondary;
      case 'danger':
        return theme.palette.red[50];
      case 'success':
        return theme.palette.green[50];
      default:
        return theme.palette.neutral[40];
    }
  }
  // text color for outline/ghost
  switch (colorScheme) {
    case 'primary':
      return theme.color.static.primary;
    case 'secondary':
      return theme.color.static.secondary;
    case 'danger':
      return theme.palette.red[50];
    case 'success':
      return theme.palette.green[50];
    default:
      return theme.color.text.TXT_2;
  }
};

const getIconSize = (size: Size): string => {
  switch (size) {
    case 'sm':
      return '16px';
    case 'lg':
      return '20px';
    default:
      return '18px';
  }
};

const SButton = styled.button<{
  $variant: Variant;
  $color: ColorScheme;
  $size: Size;
  $shape: 'rounded' | 'pill';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: auto;
  min-width: 80px;
  cursor: pointer;
  user-select: none;
  border-radius: ${({ $shape }) => ($shape === 'pill' ? '9999px' : '8px')};

  /* size - Figma 스펙 기준 */
  ${({ theme, $size }) => {
    switch ($size) {
      case 'sm':
        return {
          height: '36px',
          padding: '0 12px',
          gap: '4px',
          ...theme.font.body3_m,
        };
      case 'lg':
        return {
          height: '48px',
          padding: '0 20px',
          gap: '6px',
          ...theme.font.body1_m,
        };
      default:
        return {
          height: '40px',
          padding: '0 16px',
          gap: '6px',
          ...theme.font.body2_m,
        };
    }
  }}

  /* variant */
  ${({ theme, $variant, $color }) => {
    switch ($variant) {
      case 'solid':
        return {
          // brand/primary(밝은 색) 배경 위 글자는 label.onAccent(어두운색). danger/success 등은 흰색 유지.
          color: $color === 'primary' ? theme.color.label.onAccent : theme.color.static.white,
          background: getColorByScheme(theme, $color, 'background'),
          border: '1px solid transparent',
          '&:hover:not(:disabled)': {
            filter: 'brightness(0.95)',
          },
          '&:active:not(:disabled)': {
            filter: 'brightness(0.9)',
          },
        };
      case 'outline':
        return {
          background: 'transparent',
          color: getColorByScheme(theme, $color, 'text'),
          border: `1px solid ${$color === 'neutral' ? theme.color.line.line_2 : 'currentColor'}`,
          '&:hover:not(:disabled)': {
            background: theme.palette.neutral[10],
          },
          '&:active:not(:disabled)': {
            background: theme.palette.neutral[15],
          },
        };
      case 'ghost':
        return {
          background: 'transparent',
          color: getColorByScheme(theme, $color, 'text'),
          border: '1px solid transparent',
          '&:hover:not(:disabled)': {
            background: theme.palette.neutral[10],
          },
          '&:active:not(:disabled)': {
            background: theme.palette.neutral[15],
          },
        };
      case 'tonal':
        return {
          border: '1px solid transparent',
          background: theme.palette.neutral[15],
          color:
            $color === 'primary'
              ? theme.color.static.primary
              : $color === 'secondary'
                ? theme.color.static.secondary
                : $color === 'danger'
                  ? theme.palette.red[50]
                  : $color === 'success'
                    ? theme.palette.green[50]
                    : theme.color.text.TXT_2,
          '&:hover:not(:disabled)': {
            background: theme.palette.neutral[20],
          },
          '&:active:not(:disabled)': {
            background: theme.palette.neutral[25],
          },
        };
      default:
        return {};
    }
  }}

  /* disabled */
  &:disabled {
    cursor: not-allowed;
    opacity: 1;

    ${({ theme, $variant }) => {
      if ($variant === 'solid') {
        // 비활성 배경이 background/muted라 글자는 label/primary(밝은 계열) — Figma Box Button States=Inactive
        return {
          color: theme.color.label.primary,
          background: theme.color.buttons.disable,
          borderColor: theme.color.buttons.disable,
        };
      }
      // outline, ghost, tonal
      return {
        color: theme.color.text.TXT_1,
        background: 'transparent',
        borderColor: $variant === 'outline' ? theme.color.line.line_2 : 'transparent',
      };
    }}
  }

  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    width: ${({ $size }) => getIconSize($size)};
    height: ${({ $size }) => getIconSize($size)};

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .btn-label {
    line-height: 1;
  }
`;
